// core
import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';

import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';

// mui
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';

// components
import AccountStatusChipList from 'components/Chips/AccountStatusChipList';
import Dump from 'components/Dump';
import QPanelButton, { UnClickableStyleEnum } from 'components/QPanelButton';

const TransferAccountRow = ({
  account,
  index,
  handleContinue,
  handleChange,
  formatFinalFour,
  disabled,
  className,
  kickoffMicrodepositFlow,
}) => {
  const eligibiltyObject = useSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForTransfers(state, account),
  );
  const microdepositEligibilityValues = useSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForMicrodeposits(state, account),
  );
  const didDepositFail = microdepositEligibilityValues.isFailure;
  const { isPending } = microdepositEligibilityValues;
  const isMoveMoneyEligible = useSelector((state) =>
    fundingAccountSelectors.isAccountEligibleForCashTransfer(state, account),
  ).status;
  const accountSubtitle = formatFinalFour(account);

  const accountStatus = {
    ineligible: {
      showChip: !eligibiltyObject.isEligible,
      tooltipText: eligibiltyObject.reasonString,
    },
    depositPending: {
      enableTransferFunctions: microdepositEligibilityValues.isEligible, // Functionality is available
      showChip: microdepositEligibilityValues.status, // The status is DEPOSIT PENDING
      showFailureChipAndEnableFunctions: didDepositFail,
      showPendingChip: isPending,
      isPending,
    },
    verified: {
      showChip: isMoveMoneyEligible,
    },
  };

  const microDepositArrow = (
    <Box display='flex' flexDirection='row'>
      <AccountStatusChipList accountStatus={accountStatus} />
      <IconButton size='large'>
        <ArrowForwardIosRoundedIcon fontSize='small' />
      </IconButton>
    </Box>
  );

  const arrow = () => {
    if (
      // If the functionality is not enabled for any of these reasons!
      ((accountStatus.ineligible.showChip && !accountStatus.depositPending.enableTransferFunctions) ||
        accountStatus.depositPending.showPendingChip) &&
      !accountStatus.depositPending.showFailureChipAndEnableFunctions
    ) {
      return <AccountStatusChipList accountStatus={accountStatus} />;
    } else if (
      // If the functionality IS enabled for any of these reasons!
      (accountStatus.ineligible.showChip && accountStatus.depositPending.enableTransferFunctions) ||
      accountStatus.depositPending.showFailureChipAndEnableFunctions
    ) {
      return microDepositArrow;
    } else {
      return null;
    }
  };

  // clickStyle stuff
  let clickStyle = UnClickableStyleEnum.CLICKABLE;
  if (
    accountStatus.ineligible.showChip &&
    !accountStatus.depositPending.showChip &&
    !accountStatus.depositPending.showFailureChipAndEnableFunctions
  ) {
    clickStyle = UnClickableStyleEnum.DIM_BACKGROUND;
  }

  // Show a successful status but DO NOT style the row as clickable
  if (
    accountStatus.verified.showChip ||
    accountStatus.depositPending.showPendingChip ||
    (accountStatus.depositPending.showChip && !accountStatus.depositPending.enableTransferFunctions)
  ) {
    clickStyle = UnClickableStyleEnum.DEFAULT_BACKGROUND;
  }

  return (
    <QPanelButton
      className={className}
      id={`account-${index}`}
      title={account?.name}
      subtitle={accountSubtitle}
      onClick={() => {
        if (
          accountStatus.ineligible.showChip &&
          (accountStatus.depositPending.showChip || accountStatus.depositPending.showFailureChipAndEnableFunctions)
        ) {
          kickoffMicrodepositFlow({ id: account?.id, fundingAccountId: account?.fundingAccountId });
        } else {
          handleChange({ id: account?.id });
          handleContinue();
        }
      }}
      unClickable={clickStyle}
      disabled={disabled}
      arrow={arrow()}
      startIcon={<Dump obj={account} id='account' />}
    />
  );
};

TransferAccountRow.propTypes = {
  formatFinalFour: PropTypes.func,
  account: PropTypes.object,
  index: PropTypes.number,
  handleContinue: PropTypes.func,
  handleChange: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  kickoffMicrodepositFlow: PropTypes.func,
};

export default TransferAccountRow;
